import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BannerEdgeCard = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();

  // Helper function to get yesterday's date in Pacific timezone
  const getYesterdayInPacific = () => {
    const now = new Date();
    const pacificTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    pacificTime.setDate(pacificTime.getDate() - 1);
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, '0');
    const day = String(pacificTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchRevenueData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/revenue-data', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          source: 'BannerEdge'
        }
      });
      
      setRevenueData(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      setError('Failed to fetch revenue data');
      console.error('Error fetching revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerManualFetch = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/fetch-banneredge', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Wait a moment then fetch the updated data
      setTimeout(fetchRevenueData, 2000);
    } catch (error) {
      setError('Failed to trigger data fetch');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const calculateYesterdayRevenue = () => {
    const yesterday = getYesterdayInPacific();
    
    // Filter data to only yesterday's records
    const yesterdayData = revenueData.filter(item => {
      const itemDateString = item.date.split('T')[0];
      return itemDateString === yesterday;
    });
    
    return yesterdayData.reduce((total, item) => total + item.revenue, 0).toFixed(2);
  };

  const getYesterdayDataCount = () => {
    const yesterday = getYesterdayInPacific();
    
    // Count yesterday's records
    const yesterdayData = revenueData.filter(item => {
      const itemDateString = item.date.split('T')[0];
      return itemDateString === yesterday;
    });
    
    return yesterdayData.length;
  };

  const handleCardClick = () => {
    if (revenueData.length > 0) {
      navigate('/dashboard/banneredge-details');
    }
  };

  return (
    <div 
      className={`dashboard-card ${revenueData.length > 0 ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
      style={{ cursor: revenueData.length > 0 ? 'pointer' : 'default' }}
    >
      <div className="card-header">
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center justify-content-center" 
               style={{ 
                 height: '40px', 
                 width: '120px', 
                 backgroundColor: '#f8f9fa', 
                 borderRadius: '4px',
                 padding: '4px'
               }}>
            {!logoError ? (
              <img 
                src="/images/BannerEdge_Logo.png" 
                alt="Banner Edge" 
                style={{ 
                  maxHeight: '52px', 
                  maxWidth: '200px', 
                  objectFit: 'contain' 
                }} 
                onError={() => {
                  console.log('Banner Edge logo failed to load');
                  setLogoError(true);
                }}
                onLoad={() => {
                  console.log('Banner Edge logo loaded successfully');
                }}
              />
            ) : (
              <span style={{ 
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#495057'
              }}>
                Banner Edge
              </span>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center">
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              triggerManualFetch();
            }}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              <i className="fa-solid fa-rotate"></i>
            )}
          </button>
        </div>
      </div>
      
      <div className="card-content">
        {error && (
          <div className="alert alert-warning alert-sm mb-3">
            {error}
          </div>
        )}
        
        {loading && !revenueData.length ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Fetching revenue data...</p>
          </div>
        ) : revenueData.length > 0 ? (
          <div className="text-center py-4">
            <div className="display-4 text-success fw-bold mb-2">
              ${calculateYesterdayRevenue()}
            </div>
            <p className="text-muted mb-0">Yesterday's Revenue</p>
            <small className="text-muted">
              {getYesterdayDataCount()} conversions on {getYesterdayInPacific()}
            </small>
            <br />
            <small className="text-muted">Click to view S1 breakdown</small>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-muted">
              <p>No revenue data available</p>
              <p>
                <small>
                  Use the "Fetch Data Now" button to get the latest data
                </small>
              </p>
              <button 
                className="btn btn-primary btn-sm" 
                onClick={triggerManualFetch}
                disabled={loading}
              >
                {loading ? 'Fetching...' : 'Fetch Data Manually'}
              </button>
            </div>
          </div>
        )}
        
        {lastUpdate && !loading && (
          <div className="mt-3">
            <small className="text-muted">
              Last updated: {lastUpdate.toLocaleDateString()} at {lastUpdate.toLocaleTimeString()}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerEdgeCard; 